import React, { createContext, useContext, useEffect, useState } from 'react';
import { Platform } from 'react-native';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as LocalAuthentication from 'expo-local-authentication';
import { api, setAccessToken, setRefreshHandler } from './api';
import { registerForPush } from './push';
import { checkPin, hasPin, markSecurityAsked, securityAsked } from './security';
import { t } from './i18n';

export const BIOMETRIC_KEY = 'biometricEnabled';

export interface AuthUser {
  id: string;
  role: 'ADMIN' | 'MODERATOR' | 'LANDLORD' | 'AGENCY' | 'OWNER' | 'TENANT' | 'HANDYMAN' | 'CREATOR' | 'RESIDENT';
  phone: string;
  firstName: string;
  lastName: string;
}

interface AuthTokens {
  accessToken: string;
  refreshToken: string;
  user: AuthUser;
}

interface AuthState {
  user: AuthUser | null;
  loading: boolean;
  // Заключване: сесията е жива, но екранът е заключен до лице/отпечатък или ПИН
  locked: boolean;
  unlock: () => Promise<boolean>;
  unlockWithPin: (pin: string) => Promise<boolean>;
  // Първи вход на това устройство → екранът „Защитете приложението“ (ПИН + биометрия)
  needsSecuritySetup: boolean;
  completeSecuritySetup: () => Promise<void>;
  signIn: (tokens: AuthTokens) => Promise<void>;
  signOut: () => Promise<void>;
  // Презарежда потребителя от сървъра. Нужно е, когато ролята се смени
  // (напр. собственик поема имота си сам и става наемодател) — навигацията
  // се пренарежда по новата роля.
  refreshUser: () => Promise<void>;
}

const AuthContext = createContext<AuthState>({
  user: null,
  loading: true,
  locked: false,
  unlock: async () => false,
  unlockWithPin: async () => false,
  needsSecuritySetup: false,
  completeSecuritySetup: async () => {},
  signIn: async () => {},
  signOut: async () => {},
  refreshUser: async () => {},
});

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [locked, setLocked] = useState(false);
  const [needsSecuritySetup, setNeedsSecuritySetup] = useState(false);

  useEffect(() => {
    // Тихо подновяване при 401 (изтекъл 15-мин access токен — напр. след
    // престой на заден план). api() повтаря заявката с новия токен, така че
    // екраните просто си зареждат, вместо да замръзват.
    setRefreshHandler(async () => {
      const stored = await AsyncStorage.getItem('refreshToken');
      if (!stored) return null;
      try {
        const tokens = await api<AuthTokens>('/auth/refresh', { body: { refreshToken: stored } });
        setAccessToken(tokens.accessToken);
        await AsyncStorage.setItem('refreshToken', tokens.refreshToken);
        return tokens.accessToken;
      } catch {
        // изтекла/невалидна сесия → чист изход към входния екран
        await AsyncStorage.removeItem('refreshToken');
        setAccessToken(null);
        setUser(null);
        setLocked(false);
        return null;
      }
    });
    return () => setRefreshHandler(null);
  }, []);

  useEffect(() => {
    // При старт: опресняваме сесията с пазения refresh токен.
    // Ако биометрията е включена, екранът остава заключен до разпознаване.
    (async () => {
      try {
        const refreshToken = await AsyncStorage.getItem('refreshToken');
        if (refreshToken) {
          // Заключваме при биометрия ИЛИ зададен ПИН
          const biometric = await AsyncStorage.getItem(BIOMETRIC_KEY);
          if ((biometric === '1' || (await hasPin())) && Platform.OS !== 'web') setLocked(true);

          const tokens = await api<AuthTokens>('/auth/refresh', { body: { refreshToken } });
          setAccessToken(tokens.accessToken);
          await AsyncStorage.setItem('refreshToken', tokens.refreshToken);
          setUser(tokens.user);
          void registerForPush();
        }
      } catch {
        await AsyncStorage.removeItem('refreshToken');
        setLocked(false);
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const unlock = async (): Promise<boolean> => {
    const result = await LocalAuthentication.authenticateAsync({
      promptMessage: t('Отключете Livupp'),
      cancelLabel: t('Отказ'),
    });
    if (result.success) setLocked(false);
    return result.success;
  };

  // Резервният вход на заключения екран: ПИН-ът, зададен при първия вход
  const unlockWithPin = async (pin: string): Promise<boolean> => {
    const ok = await checkPin(pin);
    if (ok) setLocked(false);
    return ok;
  };

  const signIn = async (tokens: AuthTokens) => {
    setAccessToken(tokens.accessToken);
    await AsyncStorage.setItem('refreshToken', tokens.refreshToken);
    setUser(tokens.user);
    void registerForPush();
    // Първи вход на това устройство → предлагаме ПИН + биометрия (само
    // на телефон; в браузър заключването няма смисъл)
    if (Platform.OS !== 'web' && !(await securityAsked())) {
      setNeedsSecuritySetup(true);
    }
  };

  const completeSecuritySetup = async () => {
    await markSecurityAsked();
    setNeedsSecuritySetup(false);
  };

  const signOut = async () => {
    const refreshToken = await AsyncStorage.getItem('refreshToken');
    if (refreshToken) {
      await api('/auth/logout', { body: { refreshToken } }).catch(() => {});
    }
    await AsyncStorage.removeItem('refreshToken');
    setAccessToken(null);
    setUser(null);
    setLocked(false);
  };

  // Ролята се пази в сесията — презареждаме я от сървъра, за да се пренареди
  // навигацията (напр. собственик, който току-що е станал наемодател).
  const refreshUser = async () => {
    const refreshToken = await AsyncStorage.getItem('refreshToken');
    if (!refreshToken) return;
    try {
      const tokens = await api<AuthTokens>('/auth/refresh', { body: { refreshToken } });
      setAccessToken(tokens.accessToken);
      await AsyncStorage.setItem('refreshToken', tokens.refreshToken);
      setUser(tokens.user);
    } catch {
      // изтекла сесия — оставяме я както е; следващата заявка ще я поднови
    }
  };

  return (
    <AuthContext.Provider
      value={{ user, loading, locked, unlock, unlockWithPin, needsSecuritySetup, completeSecuritySetup, signIn, signOut, refreshUser }}
    >
      {children}
    </AuthContext.Provider>
  );
}
